/**
 * SSE Type Guards - Type-safe event discrimination.
 *
 * Usage:
 * ```ts
 * hub.subscribe(hubTopics.patient(patientId), (evt) => {
 *   if (isPatientDataChangedEvent(evt.data)) {
 *     console.log('Patient data changed:', evt.data.dataTypes);
 *   }
 *   if (isEntryAddedEvent(evt.data)) {
 *     console.log('New entry:', evt.data.entryType);
 *   }
 * });
 * ```
 */

import type { TopicComparableEventBase } from "./sse-hub";

// ============================================================
// Patient Data Events
// Topic: patient:{patientId}
// ============================================================

/**
 * General notification that patient data has changed.
 * Subscribers should refresh the specified data types.
 */
export interface PatientDataChangedEvent extends TopicComparableEventBase {
  eventName: "patient.data-changed";
  patientId: string;
  dataTypes: string[];
  sourceDeviceId?: string;
}

export function isPatientDataChangedEvent(
  evt: TopicComparableEventBase,
): evt is PatientDataChangedEvent {
  return evt.eventName === "patient.data-changed";
}

/**
 * A diary entry (meal, insulin, exercise, etc.) was added.
 */
export interface EntryAddedEvent extends TopicComparableEventBase {
  eventName: "patient.entry-added";
  patientId: string;
  entryId: string;
  entryType: string;
  timestamp: string;
  sourceDeviceId?: string;
}

export function isEntryAddedEvent(
  evt: TopicComparableEventBase,
): evt is EntryAddedEvent {
  return evt.eventName === "patient.entry-added";
}

/**
 * CGM glucose data was added.
 */
export interface CgmDataAddedEvent extends TopicComparableEventBase {
  eventName: "patient.cgm-added";
  patientId: string;
  fromTimestamp: string;
  toTimestamp: string;
  readingCount: number;
  sourceDeviceId?: string;
}

export function isCgmDataAddedEvent(
  evt: TopicComparableEventBase,
): evt is CgmDataAddedEvent {
  return evt.eventName === "patient.cgm-added";
}

// ============================================================
// Support Request Events
// Topic: patient:{patientId}:support (for patient)
// Topic: clinic:{clinicGroupId} (for clinicians)
// ============================================================

/**
 * Support request priority levels.
 */
export type SupportRequestPriority = "Low" | "Medium" | "High" | "Urgent";

/**
 * Support request status.
 */
export type SupportRequestStatus =
  | "Open"
  | "InProgress"
  | "Resolved"
  | "Closed";

/**
 * A new support request was created.
 * Sent to clinic topic for clinician notification.
 */
export interface SupportRequestCreatedEvent extends TopicComparableEventBase {
  eventName: "support.request-created";
  supportRequestId: string;
  patientId: string;
  clinicGroupId: string;
  subject: string;
  priority: SupportRequestPriority;
  createdAt: string;
}

export function isSupportRequestCreatedEvent(
  evt: TopicComparableEventBase,
): evt is SupportRequestCreatedEvent {
  return evt.eventName === "support.request-created";
}

/**
 * Support request status was updated.
 */
export interface SupportRequestUpdatedEvent extends TopicComparableEventBase {
  eventName: "support.request-updated";
  supportRequestId: string;
  patientId: string;
  newStatus: SupportRequestStatus;
  updatedBy: string;
  updatedAt: string;
}

export function isSupportRequestUpdatedEvent(
  evt: TopicComparableEventBase,
): evt is SupportRequestUpdatedEvent {
  return evt.eventName === "support.request-updated";
}

/**
 * A response was added to a support request.
 * Sent to patient topic when clinician responds.
 */
export interface SupportResponseAddedEvent extends TopicComparableEventBase {
  eventName: "support.response-added";
  supportRequestId: string;
  patientId: string;
  responderId: string;
  responderName: string;
  isClinicianResponse: boolean;
  addedAt: string;
}

export function isSupportResponseAddedEvent(
  evt: TopicComparableEventBase,
): evt is SupportResponseAddedEvent {
  return evt.eventName === "support.response-added";
}

/**
 * A clinician added a note to patient data.
 */
export interface ClinicianNoteAddedEvent extends TopicComparableEventBase {
  eventName: "support.clinician-note-added";
  patientId: string;
  clinicianId: string;
  clinicianName: string;
  noteId: string;
  addedAt: string;
}

export function isClinicianNoteAddedEvent(
  evt: TopicComparableEventBase,
): evt is ClinicianNoteAddedEvent {
  return evt.eventName === "support.clinician-note-added";
}

// ============================================================
// Relationship Events
// Topic: user:{userId}
// ============================================================

/**
 * A relationship request was received.
 */
export interface RelationshipRequestedEvent extends TopicComparableEventBase {
  eventName: "relationship.requested";
  targetUserId: string;
  requesterId: string;
  requesterName: string;
  relationshipType: string;
  requestedAt: string;
}

export function isRelationshipRequestedEvent(
  evt: TopicComparableEventBase,
): evt is RelationshipRequestedEvent {
  return evt.eventName === "relationship.requested";
}

/**
 * A relationship request was accepted.
 */
export interface RelationshipAcceptedEvent extends TopicComparableEventBase {
  eventName: "relationship.accepted";
  requesterId: string;
  acceptedUserId: string;
  acceptedUserName: string;
  relationshipId: string;
  acceptedAt: string;
}

export function isRelationshipAcceptedEvent(
  evt: TopicComparableEventBase,
): evt is RelationshipAcceptedEvent {
  return evt.eventName === "relationship.accepted";
}

/**
 * A relationship was removed.
 */
export interface RelationshipRemovedEvent extends TopicComparableEventBase {
  eventName: "relationship.removed";
  affectedUserId: string;
  relationshipId: string;
  removedBy: string;
  removedAt: string;
}

export function isRelationshipRemovedEvent(
  evt: TopicComparableEventBase,
): evt is RelationshipRemovedEvent {
  return evt.eventName === "relationship.removed";
}

// ============================================================
// User/Settings Events
// Topic: user:{userId}
// ============================================================

/**
 * User settings changed on another device.
 */
export interface UserSettingsChangedEvent extends TopicComparableEventBase {
  eventName: "user.settings-changed";
  userId: string;
  changedSettings: string[];
  sourceDeviceId?: string;
}

export function isUserSettingsChangedEvent(
  evt: TopicComparableEventBase,
): evt is UserSettingsChangedEvent {
  return evt.eventName === "user.settings-changed";
}

/**
 * Sync required notification - data changed, refresh needed.
 */
export interface SyncRequiredEvent extends TopicComparableEventBase {
  eventName: "user.sync-required";
  userId: string;
  reason: string;
  priority: "Normal" | "Immediate";
  affectedDataTypes?: string[];
}

export function isSyncRequiredEvent(
  evt: TopicComparableEventBase,
): evt is SyncRequiredEvent {
  return evt.eventName === "user.sync-required";
}

// ============================================================
// Legacy event for backward compatibility
// ============================================================

export interface SyncDataChangedEvent extends TopicComparableEventBase {
  eventName: "sync.data-changed";
  userId: string;
  changedTables?: string[];
  version?: number;
}

export function isSyncDataChangedEvent(
  evt: TopicComparableEventBase,
): evt is SyncDataChangedEvent {
  return evt.eventName === "sync.data-changed";
}

// ============================================================
// Union type of all events
// ============================================================

export type DafneEventData =
  | PatientDataChangedEvent
  | EntryAddedEvent
  | CgmDataAddedEvent
  | SupportRequestCreatedEvent
  | SupportRequestUpdatedEvent
  | SupportResponseAddedEvent
  | ClinicianNoteAddedEvent
  | RelationshipRequestedEvent
  | RelationshipAcceptedEvent
  | RelationshipRemovedEvent
  | UserSettingsChangedEvent
  | SyncRequiredEvent
  | SyncDataChangedEvent;

/**
 * All event names for type-safe switch statements.
 */
export type DafneEventName = DafneEventData["eventName"];
